import { minTouchTarget, semanticColor, space, typeStyle } from '@beautyburst/tokens';

export interface MarketOption {
  readonly id: string;
  readonly name: string;
}

export interface MarketSelectorProps {
  readonly markets: readonly MarketOption[];
  readonly selectedMarketId: string | null;
  readonly onMarketChange: (marketId: string) => void;
  readonly disabled?: boolean;
  readonly id?: string;
  readonly compact?: boolean;
}

/**
 * Market availability is supplied exclusively by the customer bootstrap API.
 * This native select is deliberately keyboard-operable and remains usable
 * without device-location permission or account access.
 */
export function MarketSelector({
  markets,
  selectedMarketId,
  onMarketChange,
  disabled = false,
  id = 'market-selector',
  compact = false,
}: MarketSelectorProps) {
  return (
    <div
      style={{
        display: 'grid',
        gap: space[1],
        minWidth: compact ? '12rem' : 'min(100%, 20rem)',
      }}
    >
      <label
        htmlFor={id}
        style={{
          ...typeStyle('label'),
          color: semanticColor.textPrimary,
          fontWeight: 700,
        }}
      >
        {compact ? 'Market' : 'Choose your market'}
      </label>
      <select
        id={id}
        data-testid="select-market"
        aria-label={compact ? 'Choose your market' : undefined}
        value={selectedMarketId ?? ''}
        disabled={disabled || markets.length === 0}
        onChange={(event) => {
          if (event.currentTarget.value !== '') onMarketChange(event.currentTarget.value);
        }}
        style={{
          minHeight: minTouchTarget,
          minWidth: minTouchTarget,
          width: '100%',
          padding: `0 ${space[3]}`,
          color: semanticColor.textPrimary,
          background: semanticColor.surfaceWhite,
          border: `1px solid ${semanticColor.borderDefault}`,
          borderRadius: '0.5rem',
          ...typeStyle('body'),
        }}
      >
        <option value="" disabled>
          {markets.length === 0 ? 'No markets are available' : 'Select a market'}
        </option>
        {markets.map((market) => (
          <option key={market.id} value={market.id}>
            {market.name}
          </option>
        ))}
      </select>
    </div>
  );
}

export function MarketSelectionRequired({
  markets,
  onMarketChange,
}: Pick<MarketSelectorProps, 'markets' | 'onMarketChange'>) {
  return (
    <section
      aria-labelledby="market-selection-title"
      style={{ display: 'grid', gap: space[4], maxWidth: '32rem' }}
    >
      <h1
        id="market-selection-title"
        style={{ ...typeStyle('headingLg'), color: semanticColor.textPrimary, margin: 0 }}
      >
        Choose a market to start shopping
      </h1>
      <p style={{ ...typeStyle('body'), color: semanticColor.textSecondary, margin: 0 }}>
        Select an available Beauty Burst market to see local products, categories, and stores.
      </p>
      <MarketSelector markets={markets} selectedMarketId={null} onMarketChange={onMarketChange} />
    </section>
  );
}
